import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Search, Trophy, Save, Crown, Medal, Calendar,
  ChevronUp, ChevronDown, Castle as CastleIcon, History, RotateCcw, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import CastleVisual from '../components/CastleVisual';
import '../components/Castle.css';

const AdminStreakPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [readers, setReaders] = useState([]);
  const [selected, setSelected] = useState(null);
  const [streakData, setStreakData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [editCastleIndex, setEditCastleIndex] = useState(0);
  const [editFill, setEditFill] = useState(0);
  const [editReason, setEditReason] = useState('');

  const [board, setBoard] = useState({ males: [], females: [] });
  const [activeTab, setActiveTab] = useState('males');

  useEffect(() => {
    if (!user?.is_admin) { navigate('/login'); return; }
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [rRes, bRes] = await Promise.all([
        api.get('/admin/readers'),
        api.get('/streak/honor-board?limit=0'),
      ]);
      setReaders(rRes.data || []);
      setBoard(bRes.data || { males: [], females: [] });
    } catch {
      toast.error('فشل تحميل البيانات');
    } finally { setLoading(false); }
  };

  const loadStreak = async (rn) => {
    try {
      const res = await api.get(`/admin/streak/${rn}`);
      setSelected(rn);
      setStreakData(res.data);
      setEditCastleIndex(res.data.castle_index ?? 0);
      setEditFill(res.data.fill_percent ?? 0);
      setEditReason('');
    } catch {
      toast.error('فشل تحميل بيانات القارئ');
    }
  };

  const handleSetCastle = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const res = await api.post(`/admin/streak/${selected}/set-castle`, {
        castle_index: Number(editCastleIndex),
        fill_percent: Number(editFill),
        reason: editReason,
      });
      toast.success(res.data.message || 'تم الحفظ');
      loadStreak(selected);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فشل التحديث');
    } finally { setSaving(false); }
  };

  const handleClearOverride = async () => {
    if (!selected) return;
    if (!window.confirm('إزالة التدخل اليدوي وإعادة الاحتساب من سجل النشاط فقط؟')) return;
    setSaving(true);
    try {
      await api.delete(`/admin/streak/${selected}/override`);
      toast.success('تم إزالة التدخل');
      loadStreak(selected);
      loadAll();
    } catch {
      toast.error('فشل الإجراء');
    } finally { setSaving(false); }
  };

  const filtered = readers.filter((r) =>
    r.name?.includes(search) || r.full_name?.includes(search) || r.reader_number?.includes(search)
  );

  if (loading) {
    return <div className="flex items-center justify-center min-h-[60vh]"><div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto" /></div>;
  }

  return (
    <div className="space-y-8" data-testid="admin-streak-page">
      <div>
        <h1 className="text-3xl md:text-4xl font-bold text-accent flex items-center gap-3">
          <CastleIcon className="w-8 h-8 text-amber-600" />
          إدارة القلاع والمواصلة القرائية
        </h1>
        <p className="text-muted-foreground mt-2">شيّد، صدّع، أو رمّم قلاع القراء — لوحة شرف ديناميكية وتحكم كامل</p>
      </div>

      {/* HONOR BOARD */}
      <Card className="p-6" data-testid="honor-board-card">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h2 className="text-xl md:text-2xl font-bold text-accent flex items-center gap-2">
            <Trophy className="w-6 h-6 text-amber-500" /> اللوحة الشرفية
          </h2>
          <div className="inline-flex rounded-xl border-2 border-primary/20 overflow-hidden">
            <button
              onClick={() => setActiveTab('males')}
              className={`px-4 py-2 text-sm font-bold transition-all ${activeTab === 'males' ? 'bg-primary text-white' : 'bg-white text-accent hover:bg-primary/5'}`}
              data-testid="board-tab-males"
            >القراء ({board.males.length})</button>
            <button
              onClick={() => setActiveTab('females')}
              className={`px-4 py-2 text-sm font-bold transition-all ${activeTab === 'females' ? 'bg-secondary text-white' : 'bg-white text-accent hover:bg-secondary/5'}`}
              data-testid="board-tab-females"
            >القارئات ({board.females.length})</button>
          </div>
        </div>

        {(activeTab === 'males' ? board.males : board.females).length === 0 ? (
          <p className="text-muted-foreground text-center py-8">لا توجد قلاع قيد التشييد بعد</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[560px] overflow-y-auto pl-1" data-testid="honor-board-list">
            {(activeTab === 'males' ? board.males : board.females).map((r, i) => {
              const Rank = i === 0 ? Crown : i < 3 ? Medal : null;
              let daysSince = null, staleness = 'fresh';
              if (r.last_active_date) {
                const last = new Date(r.last_active_date + 'T00:00:00');
                const diff = Math.floor((Date.now() - last.getTime()) / 86400000);
                daysSince = diff;
                if (diff >= 7) staleness = 'cold';
                else if (diff >= 3) staleness = 'warm';
              }
              const stalenessClass = staleness === 'cold' ? 'text-red-600 bg-red-50 border-red-200'
                : staleness === 'warm' ? 'text-amber-700 bg-amber-50 border-amber-200'
                : 'text-emerald-700 bg-emerald-50 border-emerald-200';
              return (
                <div
                  key={r.reader_number}
                  className="flex items-center gap-3 bg-gradient-to-l from-white via-white to-amber-50/40 rounded-xl p-3.5 border border-amber-100 hover:shadow-md transition-shadow"
                  data-testid={`honor-row-${r.reader_number}`}
                >
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${i === 0 ? 'bg-amber-100 text-amber-700' : i === 1 ? 'bg-gray-100 text-gray-600' : i === 2 ? 'bg-orange-100 text-orange-600' : 'bg-muted/40 text-muted-foreground'}`}>
                    {Rank ? <Rank className="w-5 h-5" /> : <span className="font-bold text-sm">{i + 1}</span>}
                  </div>
                  <CastleVisual index={r.castle_index} fillPercent={r.fill_percent} cracked={r.cracked} color={r.level_color} size="sm" />
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-accent truncate">{r.name}</p>
                    <div className="flex items-center gap-2 flex-wrap mt-0.5">
                      <span className="text-xs text-muted-foreground">{r.level_name} · {r.fill_percent}%</span>
                      {r.cracked && <span className="inline-flex items-center gap-0.5 text-[10px] text-red-600"><AlertTriangle className="w-3 h-3" />متصدعة</span>}
                      {r.last_active_date && (
                        <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full border ${stalenessClass}`} title={`آخر نشاط: ${r.last_active_date}`}>
                          <Calendar className="w-3 h-3" />
                          {daysSince === 0 ? 'اليوم' : daysSince === 1 ? 'أمس' : `قبل ${daysSince} يوم`}
                        </span>
                      )}
                    </div>
                  </div>
                  <button onClick={() => loadStreak(r.reader_number)} className="text-primary hover:underline text-xs shrink-0" data-testid={`board-edit-${r.reader_number}`}>
                    تحرير
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* SEARCH & EDITOR */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-5 lg:col-span-1">
          <div className="relative mb-4">
            <Search className="absolute right-3 top-3 w-5 h-5 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="بحث بالاسم أو الرقم..." className="pr-10 h-11" data-testid="search-readers-streak" />
          </div>
          <div className="space-y-2 max-h-[560px] overflow-y-auto">
            {filtered.slice(0, 100).map((r) => (
              <button
                key={r.reader_number}
                onClick={() => loadStreak(r.reader_number)}
                className={`w-full text-right p-3 rounded-lg border-2 transition-all ${selected === r.reader_number ? 'border-primary bg-primary/5' : 'border-transparent hover:border-primary/30 hover:bg-primary/5'}`}
                data-testid={`reader-row-${r.reader_number}`}
              >
                <p className="font-semibold text-accent">{r.name}</p>
                <p className="text-xs text-muted-foreground">{r.reader_number} • {r.gender}</p>
              </button>
            ))}
            {filtered.length === 0 && <p className="text-center text-muted-foreground py-4">لا نتائج</p>}
          </div>
        </Card>

        <Card className="p-6 lg:col-span-2" data-testid="streak-editor">
          {!selected || !streakData ? (
            <div className="text-center text-muted-foreground py-16">
              <CastleIcon className="w-12 h-12 mx-auto mb-3 text-muted-foreground/40" />
              <p>اختر قارئاً من القائمة لإدارة قلعته</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Reader header */}
              <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                  <h3 className="text-2xl font-bold text-accent">{streakData.name || streakData.reader_number}</h3>
                  <p className="text-sm text-muted-foreground">رقم: {streakData.reader_number} • {streakData.gender === 'male' ? 'ذكر' : streakData.gender === 'female' ? 'أنثى' : '-'}</p>
                </div>
                <CastleVisual
                  index={streakData.castle_index}
                  fillPercent={streakData.fill_percent}
                  cracked={streakData.cracked}
                  color={streakData.level?.color}
                  size="md"
                  levelName={streakData.level?.name}
                />
              </div>

              {/* Stats grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat label="نسبة الإتمام" value={`${streakData.fill_percent}%`} color={streakData.level?.color} />
                <Stat label="لبنات القلعة" value={`${streakData.streak_in_castle} / ${streakData.castle_size}`} />
                <Stat label="آخر نشاط" value={streakData.last_active_date || '-'} />
                <Stat label="الانقطاع المتتالي" value={`${streakData.consecutive_misses || 0} يوم`} />
                <Stat label="مجموع أيام النشاط" value={streakData.total_active_days || 0} />
                <Stat label="السلسلة الحالية" value={streakData.current_streak || 0} />
                <Stat label="أطول سلسلة" value={streakData.longest_streak || 0} />
                <Stat label="مرات التنازل" value={streakData.demotions || 0} />
              </div>

              {/* Castle override form */}
              <div className="bg-amber-50/40 border border-amber-200 rounded-xl p-5 space-y-4" data-testid="castle-override-form">
                <h4 className="font-bold text-accent flex items-center gap-2">
                  <CastleIcon className="w-5 h-5" /> ضبط يدوي للقلعة
                </h4>
                <p className="text-sm text-muted-foreground">اختر مستوى القلعة ونسبة إتمامها — يُطبَّق فوراً ويُسجَّل في السجل التاريخي.</p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-bold mb-1.5 block">القلعة (المستوى)</Label>
                    <div className="grid grid-cols-3 gap-2">
                      {(streakData.all_levels || []).map((lv) => {
                        const active = editCastleIndex === lv.index;
                        return (
                          <button
                            key={lv.level_id}
                            type="button"
                            onClick={() => setEditCastleIndex(lv.index)}
                            className={`relative overflow-hidden text-xs px-2.5 py-2 rounded-xl font-extrabold transition-all duration-300 ${active ? 'text-white scale-105 shadow-lg' : 'bg-white text-accent hover:scale-[1.03] hover:shadow-md'}`}
                            style={active
                              ? { background: `linear-gradient(135deg, ${lv.color}, ${lv.color}dd)`, boxShadow: `0 8px 20px -6px ${lv.color}80, 0 0 0 2px ${lv.color}` }
                              : { border: `2px solid ${lv.color}55`, color: '#3d2a1f' }}
                            data-testid={`castle-pick-${lv.index}`}
                          >
                            {active && (
                              <span className="absolute inset-0 opacity-30 pointer-events-none" style={{ background: `radial-gradient(circle at 20% 20%, ${lv.color}00 0%, ${lv.color}88 60%)` }} />
                            )}
                            <span className="relative z-10 drop-shadow-sm">{lv.name}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-bold mb-1.5 block">نسبة الإتمام</Label>
                    <div className="flex gap-2 items-center">
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={editFill}
                        onChange={(e) => {
                          const v = e.target.value === '' ? 0 : Math.max(0, Math.min(100, Number(e.target.value)));
                          setEditFill(v);
                        }}
                        className="h-9 w-24 text-center font-bold"
                        data-testid="castle-fill-input"
                      />
                      <span className="text-sm text-muted-foreground">%</span>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        step="1"
                        value={editFill}
                        onChange={(e) => setEditFill(Number(e.target.value))}
                        className="flex-1 h-2 accent-primary"
                        data-testid="castle-fill-range"
                      />
                    </div>
                    <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
                      <span>0%</span><span>50%</span><span>100%</span>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 items-center flex-wrap">
                  <Button
                    size="sm"
                    onClick={() => setEditFill(Math.min(100, editFill + 25))}
                    className="bg-gradient-to-br from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white shadow-md hover:shadow-lg transition-all font-bold"
                    data-testid="quick-up"
                  >
                    <ChevronUp className="w-4 h-4 ml-1" /> +25%
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => setEditFill(Math.max(0, editFill - 25))}
                    className="bg-gradient-to-br from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700 text-white shadow-md hover:shadow-lg transition-all font-bold"
                    data-testid="quick-down"
                  >
                    <ChevronDown className="w-4 h-4 ml-1" /> -25%
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => setEditFill(Math.min(100, editFill + 5))}
                    className="bg-gradient-to-br from-sky-500 to-sky-600 hover:from-sky-600 hover:to-sky-700 text-white shadow-md hover:shadow-lg transition-all font-bold"
                  >+5%</Button>
                  <Button
                    size="sm"
                    onClick={() => setEditFill(Math.max(0, editFill - 5))}
                    className="bg-gradient-to-br from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-md hover:shadow-lg transition-all font-bold"
                  >-5%</Button>
                </div>
                <Input
                  value={editReason}
                  onChange={(e) => setEditReason(e.target.value)}
                  placeholder="سبب التدخل (اختياري)"
                  className="h-10"
                  data-testid="override-reason"
                />
                <div className="flex gap-3 flex-wrap">
                  <Button onClick={handleSetCastle} disabled={saving} className="bg-primary" data-testid="set-castle-btn">
                    <Save className="w-4 h-4 ml-2" /> {saving ? 'جارٍ الحفظ...' : 'تطبيق التغيير'}
                  </Button>
                  {streakData.override && (
                    <Button onClick={handleClearOverride} disabled={saving} variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="clear-override-btn">
                      <RotateCcw className="w-4 h-4 ml-2" /> إلغاء التدخل اليدوي
                    </Button>
                  )}
                </div>
              </div>

              {/* Recent activity dates + current castle summary */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <h4 className="font-bold text-accent mb-2">آخر أيام النشاط</h4>
                  {streakData.recent_activities?.length ? (
                    <div className="flex flex-wrap gap-1.5">
                      {streakData.recent_activities.slice(0, 60).map((day) => (
                        <span key={day} className="bg-emerald-50 border border-emerald-200 text-emerald-800 text-[11px] font-medium px-2 py-0.5 rounded-full">
                          {day}
                        </span>
                      ))}
                    </div>
                  ) : <p className="text-sm text-muted-foreground">لا يوجد نشاط مسجل</p>}
                </div>
                <div>
                  <h4 className="font-bold text-accent mb-2 flex items-center gap-1"><CastleIcon className="w-4 h-4" /> الحالة الحالية</h4>
                  <div className="rounded-xl p-4 border-2 text-center" style={{ background: `linear-gradient(135deg, ${streakData.level?.color || '#c19a6b'}15, ${streakData.level?.color || '#c19a6b'}30)`, borderColor: (streakData.level?.color || '#c19a6b') + '55' }} data-testid="current-castle-summary">
                    <p className="text-xs text-muted-foreground">القلعة</p>
                    <p className="text-lg font-extrabold" style={{ color: streakData.level?.color || '#3d2a1f' }}>{streakData.level?.name || '—'}</p>
                    <p className="text-3xl font-black mt-1" style={{ color: streakData.level?.color || '#3d2a1f' }}>{streakData.fill_percent ?? 0}%</p>
                    <p className="text-[10px] text-muted-foreground mt-1">النسبة الحالية</p>
                  </div>
                </div>
              </div>

              {/* Audit log (last 5 states) — always visible */}
              <div>
                <h4 className="font-bold text-accent flex items-center gap-2 mb-2"><History className="w-4 h-4" /> آخر ٥ حالات للقلعة</h4>
                {streakData.audit_log && streakData.audit_log.length > 0 ? (
                  <div className="space-y-1.5">
                    {streakData.audit_log.slice(0, 5).map((a, i) => (
                      <div key={`${a.ts}-${i}`} className="text-xs bg-white rounded-lg p-2.5 border border-amber-200 shadow-sm" data-testid={`castle-audit-${i}`}>
                        <div className="flex justify-between gap-2">
                          <span className="font-semibold text-accent">{a.action === 'set_castle' ? `ضبط: ${a.level_name} · ${a.fill_percent}%` : 'إلغاء التدخل'}</span>
                          <span className="text-muted-foreground">{new Date(a.ts).toLocaleString('ar')}</span>
                        </div>
                        <p className="text-muted-foreground mt-0.5">بواسطة: {a.by} {a.reason && `— ${a.reason}`}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground bg-muted/20 rounded-lg p-3 border border-dashed">لا يوجد سجل بعد — أول تعديل يدوي سيظهر هنا</p>
                )}
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

const Stat = ({ label, value, color }) => (
  <div className="rounded-xl border border-amber-100 bg-white p-3">
    <p className="text-[10px] text-muted-foreground">{label}</p>
    <p className="text-lg font-extrabold" style={color ? { color } : { color: '#3d2a1f' }}>{value}</p>
  </div>
);

export default AdminStreakPage;
